'use client';

import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["withFieldset", "children", "indicator", "inline", "label", "labelFor", "hideLabel", "popover", "optionalIndicatorText", "requiredIndicatorTextForScreenReader"];
import React from 'react';
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { Group } from "./Group.js";
import { IndicatorText } from "./IndicatorText.js";
import { InlineGroup } from "./InlineGroup.js";
import { Label } from "./Label.js";
import { LabelGroup } from "./LabelGroup.js";
import { LabelInner } from "./LabelInner.js";
import { Popover } from "./Popover.js";
import { Required } from "./Required.js";
import { textClass } from "../../styles/layout-theme.js";
// Allows 'optional' or 'required' to render in specific ways, but also allow arbitrary strings
export var getIndicator = function getIndicator(indicator, optionalIndicatorText, requiredIndicatorTextForScreenReader) {
  switch (indicator) {
    case 'optional':
      return /*#__PURE__*/React.createElement(IndicatorText, {
        className: textClass('bodySmall')
      }, optionalIndicatorText);
    case 'required':
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Required, {
        "aria-hidden": "true"
      }, "*"), /*#__PURE__*/React.createElement(VisuallyHidden, null, requiredIndicatorTextForScreenReader));
    default:
      return /*#__PURE__*/React.createElement(IndicatorText, {
        className: textClass('bodySmall')
      }, "(", indicator, ")");
  }
};

/**
 * The FormGroup combines children form elements into a Form, allowing the addition of a label, optional or required indicators, and a Popover.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-formgroup--docs) |
 * [Encore site](https://encore.spotify.net/web/components/form#form-group) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/FormGroup) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32403-15523&t=NcrXkYtzM5yslqPK-1)
 */
export var FormGroup = function FormGroup(_ref) {
  var withFieldset = _ref.withFieldset,
    children = _ref.children,
    indicator = _ref.indicator,
    inline = _ref.inline,
    label = _ref.label,
    labelFor = _ref.labelFor,
    hideLabel = _ref.hideLabel,
    popover = _ref.popover,
    _ref$optionalIndicato = _ref.optionalIndicatorText,
    optionalIndicatorText = _ref$optionalIndicato === void 0 ? '(Optional)' : _ref$optionalIndicato,
    _ref$requiredIndicato = _ref.requiredIndicatorTextForScreenReader,
    requiredIndicatorTextForScreenReader = _ref$requiredIndicato === void 0 ? '(required)' : _ref$requiredIndicato,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Group, Object.assign({
    as: withFieldset ? 'fieldset' : undefined
  }, props, {
    "data-encore-id": ENCORE_DATA_IDS.FormGroup
  }), label && /*#__PURE__*/React.createElement(LabelGroup, {
    as: withFieldset ? 'legend' : undefined,
    $isHidden: !!hideLabel,
    className: textClass('bodySmallBold')
  }, withFieldset ? /*#__PURE__*/React.createElement("span", null, label, indicator && getIndicator(indicator, optionalIndicatorText, requiredIndicatorTextForScreenReader)) : /*#__PURE__*/React.createElement(Label, {
    htmlFor: labelFor
  }, /*#__PURE__*/React.createElement(LabelInner, null, label, indicator && getIndicator(indicator, optionalIndicatorText, requiredIndicatorTextForScreenReader))), popover && /*#__PURE__*/React.createElement(Popover, null, popover)), inline ? /*#__PURE__*/React.createElement(InlineGroup, null, children) : children);
};